import React, { useState } from "react";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import { useDashboardData } from "../../hooks/useDashboardData";

const SellerDashboard: React.FC = () => {
  const { user, profile, logout } = useAuthStore();
  const navigate = useNavigate();
  const [dismissedNotifications, setDismissedNotifications] = useState<string[]>([]);
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError } = useDashboardData();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch {
      // Optionally handle logout error
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar Navigation */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-900">Business Dashboard</h1>
                <p className="text-gray-600 ml-4">
                  Welcome back, {profile?.full_name || user?.email?.split('@')[0]}!
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {user?.email}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8">
            {/* Notifications Section */}
            <div className="space-y-4">
              {/* Business Dashboard Active Notification */}
              {!dismissedNotifications.includes('dashboard-active') && !dashboardError && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 relative">
                  <button
                    onClick={() => setDismissedNotifications(prev => [...prev, 'dashboard-active'])}
                    className="absolute top-4 right-4 text-green-600 hover:text-green-800"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-lg font-medium text-green-900 mb-2">Business Dashboard Active</h4>
                      <p className="text-green-800 mb-4">
                        Your business dashboard is now connected to live data. You can:
                      </p>
                      <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                        <li>Manage your complete tyre inventory</li>
                        <li>Set up installation services and pricing</li>
                        <li>Handle customer bookings and appointments</li>
                        <li>Communicate directly with customers</li>
                        <li>Track business analytics and performance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Low Stock Alert Notification */}
              {!dismissedNotifications.includes('low-stock') && dashboardData.lowStockItems.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 relative">
                  <button
                    onClick={() => setDismissedNotifications(prev => [...prev, 'low-stock'])}
                    className="absolute top-4 right-4 text-yellow-600 hover:text-yellow-800"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-yellow-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div>
                      <h4 className="text-lg font-medium text-yellow-900 mb-2">Low Stock Alert</h4>
                      <p className="text-yellow-800 mb-2">
                        You have {dashboardData.lowStockItems.length} item(s) with low stock (2 or fewer units).
                      </p>
                      <button
                        onClick={() => navigate('/business/inventory')}
                        className="text-sm text-yellow-700 hover:text-yellow-900 font-medium underline"
                      >
                        Manage Inventory →
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Stats Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <div
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:bg-blue-50 transition"
                onClick={() => navigate("/business/inventory")}
                title="View and manage your inventory"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Inventory Items</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardLoading ? "..." : dashboardData.stats.inventoryCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                <button
                  className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-center h-full flex flex-col justify-between"
                  onClick={() => navigate("/business/inventory")}
                  title="Add new inventory items"
                >
                  <svg className="w-8 h-8 text-blue-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <p className="text-sm font-medium text-gray-900">Add Inventory</p>
                </button>

                <button
                  className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-center h-full flex flex-col justify-between"
                  onClick={() => navigate("/business/reviews")}
                  title="View and manage your reviews"
                >
                  <svg className="w-8 h-8 text-purple-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-900">Reviews</p>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-1 gap-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Low Stock Alerts</h3>
                <div className="space-y-4">
                  {dashboardLoading ? (
                    <div className="text-center py-4 text-gray-500">Loading inventory...</div>
                  ) : dashboardData.lowStockItems.length > 0 ? (
                    dashboardData.lowStockItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div>
                          <p className="font-medium text-gray-900">{item.brand} {item.model}</p>
                          <p className="text-sm text-gray-600">{item.size}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-orange-600">{item.stock_quantity} left</p>
                          <button
                            onClick={() => navigate('/business/inventory')}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Reorder
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <p className="text-sm">All inventory items are well stocked</p>
                      <button
                        onClick={() => navigate('/business/inventory')}
                        className="text-sm text-blue-600 hover:text-blue-800 mt-2"
                      >
                        Manage inventory
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Error Notice (only if there's an error) */}
            {dashboardError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-red-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <h4 className="text-lg font-medium text-red-900 mb-2">Error Loading Dashboard Data</h4>
                    <p className="text-red-800">{dashboardError}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SellerDashboard;
